import { useEffect, useState } from 'react';
import { axiosClient } from '../../auth-lib';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../design-system';
import '../styles/CreateEditOrganization.css';

function CreateEditOrganization() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    industry: '',
    contactEmail: '',
    contactPhone: '',
    subscription: { plan: 'free', status: 'active' },
  });
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrganization = async () => {
    try {
      const response = await axiosClient.get(`/organizations/${id}`);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (id) {
        await axiosClient.put(`/organizations/${id}`, formData);
        toast.success('Organization updated');
      } else {
        await axiosClient.post('/organizations', formData);
        toast.success('Organization created');
      }
      navigate('/admin/organizations');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save organization');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Loading…" subtitle="">
        <Card><Skeleton.Text lines={5} /></Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={id ? 'Edit Organization' : 'Create Organization'}
      subtitle={id ? 'Update organization details' : 'Add a new organization to the platform'}
    >
      <form onSubmit={handleSubmit} className="org-form">
        {error && <div className="form-error" role="alert">{error}</div>}

        <Card>
          <Card.Header title="Basic Information" />
          <div className="form-fields">
            <Input label="Organization Name *" name="name" value={formData.name} onChange={handleChange} required />
            <Input label="Slug" name="slug" value={formData.slug} onChange={handleChange} placeholder="e.g., techcorp-solutions" />
            <div className="ds-field">
              <label className="ds-field-label">Description</label>
              <textarea className="ds-input" name="description" value={formData.description} onChange={handleChange} rows="3" placeholder="Brief description of the organization" />
            </div>
            <Input label="Industry" name="industry" value={formData.industry} onChange={handleChange} placeholder="e.g., Technology, Finance, Healthcare" />
          </div>
        </Card>

        <Card>
          <Card.Header title="Contact Information" />
          <div className="form-fields">
            <Input label="Email" type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} placeholder="contact@example.com" />
            <Input label="Phone" type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} placeholder="+91-XXXXXXXXXX" />
          </div>
        </Card>

        <Card>
          <Card.Header title="Subscription" />
          <div className="form-row">
            <Select label="Plan" name="subscription.plan" value={formData.subscription.plan} onChange={handleChange}>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </Select>
            <Select label="Status" name="subscription.status" value={formData.subscription.status} onChange={handleChange}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
        </Card>

        <div className="form-actions">
          <Button type="submit" variant="primary">{id ? 'Update Organization' : 'Create Organization'}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/organizations')}>Cancel</Button>
        </div>
      </form>
    </AdminLayout>
  );
}

export default CreateEditOrganization;
